import { angleDelta, bearingDeg, cardinalIt, distanceM, type LatLon } from '../lib/geo';
import { startCompass, type CompassProblem } from '../lib/compass';
import { NETWORKS, NETWORK_ORDER } from '../lib/operators';
import { rankSites } from '../lib/rank';
import { h, fmtDeg, fmtKm } from './dom';
import { getState, setState, subscribe, selectedSite } from './state';
import { radioLabel } from './site-sheet';

const ALIGNED_DEG = 3;

export function mountOnsite(root: HTMLElement) {
  let heading: number | null = null;
  let problem: CompassProblem | null = null;
  let pos: (LatLon & { acc: number }) | null = null;
  let geoError: string | null = null;
  let stopCompass: (() => void) | null = null;
  let watchId: number | null = null;

  const arrow = h('div', { class: 'arrow', 'aria-hidden': 'true' });
  arrow.innerHTML = '<svg viewBox="0 0 100 100"><path d="M50 6 78 80 50 64 22 80Z"/></svg>';
  const turn = h('p', { class: 'turn', 'aria-live': 'polite' });
  const info = h('div', { class: 'onsite-info' });
  const body = h('div', { class: 'onsite' });
  root.append(body);

  function startGps() {
    if (watchId !== null || !('geolocation' in navigator)) return;
    watchId = navigator.geolocation.watchPosition(
      p => { pos = { lat: p.coords.latitude, lon: p.coords.longitude, acc: p.coords.accuracy }; geoError = null; update(); },
      e => { geoError = e.code === e.PERMISSION_DENIED ? 'Posizione non autorizzata: uso la casa come punto di partenza.' : 'GPS non disponibile: uso la casa come punto di partenza.'; update(); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
  }

  function stopAll() {
    stopCompass?.();
    stopCompass = null;
    if (watchId !== null) navigator.geolocation.clearWatch(watchId);
    watchId = null;
  }

  async function enableCompass() {
    stopCompass?.();
    problem = null;
    stopCompass = await startCompass((deg, reason) => {
      heading = deg;
      problem = reason ?? null;
      update();
    });
    render();
  }

  function origin(): (LatLon & { acc?: number; fromHome?: boolean }) | null {
    if (pos) return pos;
    const home = getState().settings.home;
    return home ? { ...home, fromHome: true } : null;
  }

  function picker() {
    const st = getState();
    const home = st.settings.home;
    if (!home) return h('p', {}, 'Imposta prima la casa nella Mappa.');
    const ranked = rankSites(home, st.sites, new Map([...st.los].map(([k, v]) => [k, v.status])), 2);
    return h('div', { class: 'picker' },
      h('p', { class: 'muted' }, 'Scegli verso quale antenna puntare:'),
      ...NETWORK_ORDER.filter(n => ranked[n]?.length).flatMap(n =>
        ranked[n]!.map(s =>
          h('button', { class: 'rank-item', onclick: () => setState({ selectedSiteId: s.id }) },
            h('span', { class: 'dot', style: `background:${NETWORKS[n].color}` }),
            h('strong', {}, n),
            h('span', {}, `${fmtKm(s.distanceM)} · ${fmtDeg(s.bearing)} ${cardinalIt(s.bearing)}`),
          ),
        ),
      ),
    );
  }

  function render() {
    const site = selectedSite();
    body.replaceChildren();
    if (!site) {
      body.append(h('h2', {}, 'Sul posto'), picker());
      return;
    }
    startGps();
    body.append(
      h('div', { class: 'onsite-head' },
        h('span', { class: 'dot', style: `background:${NETWORKS[site.network].color}` }),
        h('div', {}, h('h2', {}, site.network), h('p', { class: 'muted small' }, [site.name, radioLabel(site.radios)].filter(Boolean).join(' · '))),
        h('button', { class: 'link', onclick: () => setState({ selectedSiteId: undefined }) }, 'Cambia'),
      ),
      h('div', { class: 'dial' }, arrow),
      turn,
      info,
      h('div', { class: 'actions center' },
        stopCompass ? null : h('button', { class: 'primary', onclick: enableCompass }, 'Attiva bussola'),
        h('button', {
          onclick: () => setState({
            tab: 'misure',
            draft: { network: site.network, siteId: site.id, headingDeg: heading ?? undefined, lat: pos?.lat, lon: pos?.lon },
          }),
        }, 'Salva misura qui'),
      ),
      h('p', { class: 'warn-box' }, 'Il palo metallico e le parti metalliche del tetto disturbano la bussola: tieni il telefono a circa un metro dal palo e controlla con un punto di riferimento visivo (campanile, capannone, strada) nella stessa direzione.'),
    );
    update();
  }

  function update() {
    const site = selectedSite();
    if (!site) return;
    const o = origin();
    const target = o ? bearingDeg(o, site) : null;
    info.replaceChildren(
      target !== null
        ? h('div', { class: 'big-numbers' },
            h('div', {}, h('strong', {}, `${fmtDeg(target)} ${cardinalIt(target)}`), h('span', {}, 'direzione dell’antenna')),
            h('div', {}, h('strong', {}, fmtKm(distanceM(o!, site))), h('span', {}, 'distanza')),
          )
        : h('p', {}, 'Imposta la casa o attiva la posizione.'),
      h('p', { class: 'muted small' },
        geoError ?? (pos ? `Posizione GPS ±${Math.round(pos.acc)} m` : o?.fromHome ? 'Calcolo dalla casa (in attesa del GPS)…' : ''),
        heading !== null ? ` · bussola ${fmtDeg(heading)}` : '',
      ),
    );
    if (problem) {
      arrow.classList.add('off');
      turn.textContent = problem === 'permesso-negato'
        ? 'Bussola non autorizzata. Usa la direzione in gradi qui sotto e un riferimento visivo.'
        : 'Bussola non disponibile su questo dispositivo. Usa la direzione in gradi qui sotto e un riferimento visivo.';
      return;
    }
    if (heading === null || target === null) {
      arrow.classList.add('off');
      arrow.style.transform = '';
      turn.textContent = stopCompass ? 'In attesa della bussola…' : 'Tocca “Attiva bussola” e tieni il telefono in piano.';
      return;
    }
    const d = angleDelta(target, heading);
    arrow.classList.remove('off');
    arrow.classList.toggle('aligned', Math.abs(d) <= ALIGNED_DEG);
    arrow.style.transform = `rotate(${d}deg)`;
    turn.textContent = Math.abs(d) <= ALIGNED_DEG ? 'Allineato' : `Gira di ${Math.round(Math.abs(d))}° a ${d > 0 ? 'destra' : 'sinistra'}`;
  }

  subscribe((s, p) => {
    if (s.tab !== 'posto' && p.tab === 'posto') stopAll();
    if (s.tab === 'posto' && (p.tab !== 'posto' || s.selectedSiteId !== p.selectedSiteId || s.sites !== p.sites || s.settings !== p.settings)) render();
  });
  if (getState().tab === 'posto') render();
}
